/**
 * Gate de arranque.
 * Prod: ticket → invite → workspace_embed → entitlements (no source:url) → offline → deny.
 * Lab: Supremo/simulate (ignora udvToken/plan query legacy); carga por manifestUrl.
 */
import { ACCESS_REASONS, ENTITLEMENT_SOURCES, UDV_PERSONAS, UDV_PLANS } from './constants'
import {
  clearEntitlements,
  loadEntitlements,
  loadControllerLinks,
  resolveOriginLinkEntitlements,
  saveEntitlements,
} from './entitlements'
import {
  parseInviteFromSearchParams,
  parseTicketFromSearchParams,
  parseWorklistFromSearchParams,
  parseTokenFromSearchParams,
  parseEdgeIdFromSearchParams,
  parseElevationTokenFromSearchParams,
} from './parsePlanQuery'
import {
  getEffectivePlan,
  getEffectiveShellHint,
  getUdvMode,
  isDeveloperMode,
  resolveToken,
} from './resolveAccess'
import { getStoredPersona, resolvePersona, setStoredPersona } from './persona'
import {
  getTicketClinicalEntitlements,
  isClinicalPlan,
  resolveTicketClinicalElevation,
} from './clinicalElevation'
import { loadControllerSession } from '../services/controllerAuth'
import { isWorkspaceEmbedContext } from './workspaceEmbed'
import { matchPlatformElevationSession } from './platformElevationSession'
import {
  parseToolbarQueryParam,
  TOOLBAR_LAYOUTS,
} from '../config/pacsConfig'

/**
 * Shell de primer paint para ?t= portal/pending.
 * Evita flash Studio (DEFAULT PACS) antes del resolve async.
 * No lee ?shell= de la URL (share-safe). Si hay entitlements de ticket, los respeta.
 * @param {object|null|undefined} ent
 * @returns {string}
 */
function ticketPendingShellHint(ent) {
  if (ent && ent.source === ENTITLEMENT_SOURCES.TICKET && ent.shell) {
    const parsed = parseToolbarQueryParam(ent.shell)
    if (parsed) return parsed
  }
  return TOOLBAR_LAYOUTS.CLASSIC
}

/**
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   entitlements?: object|null,
 *   hasLocalStudies?: boolean,
 *   isEmbedded?: boolean,
 * }} [opts]
 */
export function assertAccess(opts = {}) {
  const mode = getUdvMode()
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()
  const urlTokenStr = parseTokenFromSearchParams(params)
  const urlTokenMeta = resolveToken(urlTokenStr)
  const ticketId = parseTicketFromSearchParams(params)
  const worklistId = parseWorklistFromSearchParams(params)
  const inviteId = parseInviteFromSearchParams(params)
  const persona = resolvePersona({
    searchParams: params,
    entitlements: ent,
    persist: true,
  })
  const hasLocal = opts.hasLocalStudies === true
  const embedCtx = isWorkspaceEmbedContext({
    searchParams: params,
    isEmbedded: opts.isEmbedded,
  })

  if (isDeveloperMode()) {
    // Limpiar cache legacy source:url para no forzar lite en lab
    let labEnt = ent
    if (labEnt && labEnt.source === ENTITLEMENT_SOURCES.URL) {
      clearEntitlements()
      labEnt = null
    }
    const plan = getEffectivePlan({
      searchParams: params,
      tokenMeta: urlTokenMeta || undefined,
      entitlements: labEnt,
    })
    // ?t= share-safe: classic pending (no Studio del lab simulate) hasta resolve.
    const shellHint = ticketId
      ? ticketPendingShellHint(labEnt)
      : getEffectiveShellHint({
          searchParams: params,
          tokenMeta: urlTokenMeta || undefined,
          entitlements: labEnt,
        })
    return {
      ok: true,
      mode,
      plan,
      shellHint,
      tokenMeta:
        urlTokenMeta || (labEnt && labEnt.udvToken ? resolveToken(labEnt.udvToken) : null),
      entitlements: labEnt,
      source: ticketId
        ? 'ticket_pending'
        : worklistId
          ? 'worklist_pending'
          : inviteId
            ? 'invite_pending'
            : labEnt
              ? labEnt.source
              : 'developer',
      ticketId: ticketId || null,
      worklistId: worklistId || null,
      inviteId: inviteId || null,
      persona,
    }
  }

  // --- production ---

  const originEdgeId = parseEdgeIdFromSearchParams(params)
  const originEnt = originEdgeId
    ? resolveOriginLinkEntitlements(originEdgeId, loadControllerLinks())
    : null

  // Origen + vínculo Controller → plan de esa clínica (prioridad sobre cache/token).
  if (originEnt) {
    const plan = getEffectivePlan({
      searchParams: params,
      entitlements: originEnt,
    })
    const shellHint = getEffectiveShellHint({
      searchParams: params,
      entitlements: originEnt,
    })
    saveEntitlements(originEnt)
    return {
      ok: true,
      mode,
      plan,
      shellHint,
      tokenMeta: originEnt.udvToken ? resolveToken(originEnt.udvToken) : null,
      entitlements: originEnt,
      source: ENTITLEMENT_SOURCES.ORIGIN_LINK,
      ticketId: ticketId || null,
      inviteId: inviteId || null,
      persona: UDV_PERSONAS.CLINICAL,
      originEdgeId,
    }
  }

  // Elevación plataforma canjeada en esta pestaña (tras strip de ?elev=).
  if (ticketId) {
    const plat = matchPlatformElevationSession(ticketId)
    if (plat && isClinicalPlan(plat.plan)) {
      const platEnt = {
        plan: plat.plan,
        shell: plat.shell,
        pacsId: null,
        udvToken: null,
        edgeId: plat.edgeId || originEdgeId || null,
        clinicName: null,
        source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
      }
      return {
        ok: true,
        mode,
        plan: plat.plan,
        shellHint: getEffectiveShellHint({ entitlements: platEnt }),
        tokenMeta: null,
        entitlements: platEnt,
        source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
        ticketId,
        inviteId: null,
        persona: UDV_PERSONAS.CLINICAL,
        ticketElevated: true,
        originEdgeId: originEdgeId || plat.edgeId || null,
      }
    }
  }

  // ?elev= pendiente de canje async: no forzar portal (cold start).
  const elevPending = parseElevationTokenFromSearchParams(params)
  if (ticketId && elevPending) {
    return {
      ok: true,
      mode,
      plan: UDV_PLANS.LITE,
      shellHint: null,
      tokenMeta: null,
      entitlements: ent,
      source: 'elev_pending',
      ticketId,
      inviteId: null,
      persona: UDV_PERSONAS.CLINICAL,
      elevPending: true,
      originEdgeId: originEdgeId || null,
    }
  }

  // Worklist clínico ?w= — prioriza sobre portal/ticket URL (CONTRATO-VIEWER-WORKLIST).
  if (worklistId) {
    return {
      ok: true,
      mode,
      plan: UDV_PLANS.LITE,
      shellHint: null,
      tokenMeta: null,
      entitlements: ent,
      source: 'worklist_pending',
      ticketId: ticketId || null,
      worklistId,
      inviteId: null,
      persona: UDV_PERSONAS.CLINICAL,
      originEdgeId: originEdgeId || null,
    }
  }

  // Origen sin vínculo: portal (ticket compartido / share-safe).
  if (originEdgeId && !urlTokenStr) {
    if (ticketId) {
      return {
        ok: true,
        mode,
        plan: UDV_PLANS.PATIENT,
        shellHint: ticketPendingShellHint(ent),
        tokenMeta: null,
        entitlements: ent,
        source: 'ticket_portal',
        ticketId,
        inviteId: null,
        persona: UDV_PERSONAS.PORTAL,
        originEdgeId,
      }
    }
  }

  // Ticket ?t= antes que udvToken en URL (links compartidos pueden traer params staff).
  if (ticketId) {
    const elevation = resolveTicketClinicalElevation(
      ent,
      loadControllerSession()
    )
    const clinicalEnt = getTicketClinicalEntitlements(ent)
    const strippedParams = new URLSearchParams('')
    if (elevation.elevated && clinicalEnt) {
      const plan = getEffectivePlan({
        searchParams: strippedParams,
        entitlements: clinicalEnt,
      })
      return {
        ok: true,
        mode,
        plan,
        shellHint:
          getEffectiveShellHint({ entitlements: clinicalEnt }) ||
          getEffectiveShellHint({ searchParams: strippedParams, entitlements: ent }),
        tokenMeta: clinicalEnt.udvToken ? resolveToken(clinicalEnt.udvToken) : null,
        entitlements: clinicalEnt,
        source: 'ticket_clinical',
        ticketId,
        inviteId: null,
        persona: UDV_PERSONAS.CLINICAL,
        ticketElevated: true,
      }
    }
    return {
      ok: true,
      mode,
      plan: UDV_PLANS.PATIENT,
      shellHint: ticketPendingShellHint(ent),
      tokenMeta: null,
      entitlements: ent,
      source: 'ticket_pending',
      ticketId,
      inviteId: null,
      persona: UDV_PERSONAS.PORTAL,
    }
  }

  // Deep-link legacy udvToken: no autoriza en production (ni en lab para plan).
  // Lab usa Supremo/simulate; carga de estudio por manifestUrl es independiente.
  // docs/CONTRATO-UDV-ELEVATION.md · docs/MANUAL-0.20.66-TICKET-GATE-PROD.md

  // Invite clínico (?udvInvite=) — resolve async; acceso temporal + login
  if (inviteId) {
    if (ent && ent.plan && ent.source === 'invite') {
      const plan = getEffectivePlan({
        searchParams: params,
        entitlements: ent,
      })
      const shellHint = getEffectiveShellHint({
        searchParams: params,
        entitlements: ent,
      })
      return {
        ok: true,
        mode,
        plan,
        shellHint,
        tokenMeta: null,
        entitlements: ent,
        source: 'invite_pending',
        ticketId: null,
        inviteId,
        persona: UDV_PERSONAS.CLINICAL,
        loginRequired: true,
      }
    }
    return {
      ok: true,
      mode,
      plan: UDV_PLANS.LITE,
      shellHint: null,
      tokenMeta: null,
      entitlements: ent,
      source: 'invite_pending',
      ticketId: null,
      inviteId,
      persona: UDV_PERSONAS.CLINICAL,
      loginRequired: true,
    }
  }

  // Workspace embed (iframe + elev canjeado / pending). Auth-only; estudio vía pacs+studyId.
  if (embedCtx) {
    if (
      ent &&
      ent.plan &&
      ent.source === ENTITLEMENT_SOURCES.WORKSPACE_EMBED &&
      isClinicalPlanSafe(ent.plan)
    ) {
      const plan = getEffectivePlan({
        searchParams: params,
        entitlements: ent,
      })
      const shellHint = getEffectiveShellHint({
        searchParams: params,
        entitlements: ent,
      })
      return {
        ok: true,
        mode,
        plan,
        shellHint,
        tokenMeta: null,
        entitlements: ent,
        source: ENTITLEMENT_SOURCES.WORKSPACE_EMBED,
        ticketId: null,
        inviteId: null,
        persona: UDV_PERSONAS.CLINICAL,
      }
    }
    return {
      ok: true,
      mode,
      plan: UDV_PLANS.LITE,
      shellHint: null,
      tokenMeta: null,
      entitlements: ent,
      source: 'workspace_embed_pending',
      ticketId: null,
      inviteId: null,
      persona: UDV_PERSONAS.CLINICAL,
      workspaceEmbedPending: true,
    }
  }

  // Entitlements de sesión Controller / cache
  // Ignorar cache legacy source:url (deep-link plataforma viejo).
  // WORKSPACE_EMBED solo vale dentro de iframe Workspace (no top-level).
  let sessionEnt = ent
  if (sessionEnt && sessionEnt.plan && sessionEnt.source === ENTITLEMENT_SOURCES.URL) {
    clearEntitlements()
    sessionEnt = null
  }
  if (
    sessionEnt &&
    sessionEnt.plan &&
    sessionEnt.source === ENTITLEMENT_SOURCES.WORKSPACE_EMBED &&
    !embedCtx
  ) {
    clearEntitlements()
    sessionEnt = null
  }
  if (sessionEnt && sessionEnt.plan) {
    let tokenMeta = null
    if (sessionEnt.udvToken) {
      tokenMeta = resolveToken(sessionEnt.udvToken)
      if (!tokenMeta) {
        return {
          ok: false,
          reason: ACCESS_REASONS.INVALID_TOKEN,
          mode,
          plan: 'lite',
          shellHint: null,
          tokenMeta: null,
          entitlements: sessionEnt,
          persona: persona || getStoredPersona(),
        }
      }
      if (tokenMeta.enabled === false) {
        return {
          ok: false,
          reason: ACCESS_REASONS.DISABLED_TOKEN,
          mode,
          plan: 'lite',
          shellHint: null,
          tokenMeta,
          entitlements: sessionEnt,
          persona: persona || getStoredPersona(),
        }
      }
    }
    const plan = getEffectivePlan({
      searchParams: params,
      tokenMeta: tokenMeta || undefined,
      entitlements: sessionEnt,
    })
    const shellHint = getEffectiveShellHint({
      searchParams: params,
      tokenMeta: tokenMeta || undefined,
      entitlements: sessionEnt,
    })
    return {
      ok: true,
      mode,
      plan,
      shellHint,
      tokenMeta,
      entitlements: sessionEnt,
      source: sessionEnt.source || 'controller',
      persona:
        sessionEnt.source === 'ticket'
          ? UDV_PERSONAS.PORTAL
          : persona || UDV_PERSONAS.CLINICAL,
      loginRequired: sessionEnt.source === 'invite',
    }
  }

  // 5) Offline local (estudios en dispositivo)
  // Sin invite/sesión clínica → modo portal paciente (no lite con chrome completo).
  if (hasLocal) {
    const stored = getStoredPersona()
    const isClinical =
      stored === UDV_PERSONAS.CLINICAL || persona === UDV_PERSONAS.CLINICAL
    const offlinePersona = isClinical
      ? UDV_PERSONAS.CLINICAL
      : UDV_PERSONAS.PORTAL
    if (!isClinical) {
      setStoredPersona(UDV_PERSONAS.PORTAL)
    }
    return {
      ok: true,
      mode,
      plan: isClinical
        ? (ent && ent.plan) || UDV_PLANS.LITE
        : UDV_PLANS.PATIENT,
      shellHint: null,
      tokenMeta: null,
      entitlements: ent,
      source: 'offline_local',
      persona: offlinePersona,
    }
  }

  // 6) Nada
  return {
    ok: false,
    reason: ACCESS_REASONS.MISSING_ENTITLEMENTS,
    mode,
    plan: 'lite',
    shellHint: null,
    tokenMeta: null,
    entitlements: null,
    persona: persona || getStoredPersona(),
  }
}

/**
 * @param {string|null|undefined} plan
 * @returns {boolean}
 */
function isClinicalPlanSafe(plan) {
  const p = plan != null ? String(plan).trim().toLowerCase() : ''
  return (
    p === UDV_PLANS.LITE ||
    p === UDV_PLANS.PRO ||
    p === UDV_PLANS.PREMIUM ||
    p === UDV_PLANS.SUPREMO ||
    p === UDV_PLANS.DEMO
  )
}
